import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import './LoginPage.css';

export const ResetPasswordPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!token) {
      setError('Invalid reset token');
      return;
    }

    setLoading(true);
    try {
      await authAPI.resetPassword(token, password);
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h1>🎉 Password Reset Successful!</h1>
            <h2>Your password has been updated</h2>
          </div>
          
          <div className="success-message" style={{ textAlign: 'center', padding: '20px' }}>
            ✅ Your password has been successfully reset. You will be redirected to the login page in a few seconds.
          </div>
          
          <button 
            onClick={() => navigate('/login')}
            className="login-button"
            style={{ marginTop: '16px' }}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>🔐 Reset Your Password</h1>
          <h2>Enter your new password below</h2>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="error-message">
              ⚠️ {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="password">New Password:</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              placeholder="Enter your new password (min. 6 characters)"
              minLength={6}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password:</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={loading}
              placeholder="Confirm your new password"
              minLength={6}
            />
          </div>

          <button
            type="submit"
            className="login-button"
            disabled={loading || !password || !confirmPassword}
          >
            {loading ? '🔄 Updating Password...' : '🔐 Update Password'}
          </button>

          <div className="login-footer">
            <button 
              type="button"
              className="reset-link"
              onClick={() => navigate('/login')}
              disabled={loading}
            >
              ← Back to Login
            </button>
          </div>
        </form>

        <div className="security-notice">
          <p>🔒 Your new password will be encrypted and stored securely.</p>
        </div>
      </div>
    </div>
  );
};
